import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Banner, cautionVariant } from "./index.js";
import { Frame, type BBox } from "./Frame.js";
import { Highlighter } from "./Highlighter.js";
import { MarkdownText } from "./MarkdownText.js";
import { Icon } from "./Icon.js";
import { mediaUrl } from "../lib/api.js";
import { mmss, type StepWithCandidates } from "../lib/demo.js";
import { bboxToFrame } from "../lib/bbox.js";
import { disposeVideo } from "../lib/frames.js";

function toBBox(step: StepWithCandidates): BBox | null {
  return bboxToFrame(step.bbox, step.objectLabel);
}

/**
 * STILL-FIRST step media, shared by the document (.sline) and paged (.stage-img) reader layouts
 * so they can never drift. Renders the step's lightweight keyframe JPEG (step.imageUrl) with the
 * read-only official Highlighter overlay — NEVER the heavy original video on load. For video-flow
 * manuals a "▶ 動画でこの手順を見る" button lazily mounts ONE inline <video> on tap (seeked to the
 * step), torn down via disposeVideo so iOS Safari reclaims the decoder.
 *
 * Doc-mode sizing is ORIENTATION-AWARE: portrait stills float left of the text (a printed-manual
 * cut-in figure) while landscape screen-recording keyframes — which would collapse to a strip in
 * a float — become a full-width figure block. Every still also zooms to a lightbox on tap, since
 * screen recordings carry dense UI text that no inline size can make legible.
 */
export function StepMedia({
  step,
  variant,
  videoSrc,
  videoOpen,
  onToggleVideo,
  eager,
}: {
  step: StepWithCandidates;
  variant: "doc" | "paged";
  /** resolved original-video URL (or null for image-flow / no video) */
  videoSrc: string | null;
  videoOpen: boolean;
  onToggleVideo: () => void;
  eager?: boolean;
}) {
  const img = mediaUrl(step.imageUrl);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Callback ref: when the <video> detaches (collapse / step change / unmount), React calls this
  // with null — release the decoder so iOS Safari reclaims it (one live <video> max).
  const setVideoEl = useCallback((el: HTMLVideoElement | null) => {
    if (el === null && videoRef.current) disposeVideo(videoRef.current);
    videoRef.current = el;
  }, []);

  const doc = variant === "doc";
  // Orientation from the loaded keyframe (portrait assumed until known — avoids a wide flash).
  const [ratio, setRatio] = useState<number | null>(null);
  const landscape = (ratio ?? 0.75) > 1.15;

  // Lightbox (tap-to-enlarge)
  const [zoom, setZoom] = useState(false);
  const zoomBtnRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (zoom) closeRef.current?.focus();
  }, [zoom]);
  // Lock body scroll while zoomed — otherwise wheel/touch scrolls the document behind the
  // overlay (and in the Reader's doc mode the IntersectionObserver keeps advancing 読了).
  useEffect(() => {
    if (!zoom) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [zoom]);
  function closeZoom() {
    setZoom(false);
    zoomBtnRef.current?.focus();
  }

  // Video⇄image toggle replaces its own trigger — move focus onto the counterpart so keyboard
  // users aren't dropped on <body>.
  const videoBackBtnRef = useRef<HTMLButtonElement>(null);
  const videoOpenBtnRef = useRef<HTMLButtonElement>(null);
  const wasVideoOpenRef = useRef(videoOpen);
  useEffect(() => {
    if (videoOpen === wasVideoOpenRef.current) return;
    wasVideoOpenRef.current = videoOpen;
    requestAnimationFrame(() => (videoOpen ? videoBackBtnRef.current : videoOpenBtnRef.current)?.focus());
  }, [videoOpen]);

  const docFigure: CSSProperties = landscape
    ? { width: "100%", maxWidth: 560, margin: "2px 0 14px" } // block figure — a float would crush 16:9 stills
    : { float: "left", width: "min(200px, 42vw)", margin: "0 18px 8px 0", maxWidth: "100%" }; // 42vw: keep a readable text column beside the float on phones

  if (videoOpen && videoSrc) {
    return (
      <div
        style={
          doc
            ? { width: "100%", maxWidth: 560, margin: "2px 0 14px" } // video is (almost) always landscape
            : { position: "relative", width: "min(78%,520px)" }
        }
      >
        <video
          ref={setVideoEl}
          src={videoSrc}
          controls
          playsInline
          preload="metadata"
          crossOrigin="anonymous"
          onLoadedMetadata={() => {
            const v = videoRef.current;
            if (!v) return;
            try {
              v.currentTime = Math.max(0, (step.clipStartMs ?? step.videoTimecodeMs ?? 0) / 1000);
            } catch {
              /* metadata not ready */
            }
          }}
          style={{ width: "100%", borderRadius: 14, background: "#000", boxShadow: "0 20px 50px -16px rgba(0,0,0,.6)" }}
        />
        <button ref={videoBackBtnRef} className="statuspill" onClick={onToggleVideo} style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon name="frame" size={14} /> 画像に戻る
        </button>
      </div>
    );
  }

  const frame = (
    <Frame
      paper
      src={img}
      alt={step.title}
      eager={eager}
      onRatio={setRatio}
      cap={step.videoTimecodeMs != null ? mmss(step.videoTimecodeMs) : undefined}
      bbox={toBBox(step)}
      className={doc ? "pic" : undefined}
      style={doc ? { width: "100%", aspectRatio: "3/4" } : { width: "100%" }}
    >
      {step.highlights?.strokes?.length ? (
        <Highlighter src={null} value={step.highlights.strokes} readOnly style={{ position: "absolute", inset: 0 }} />
      ) : null}
    </Frame>
  );

  return (
    <div style={doc ? docFigure : { position: "relative", width: "min(70%,360px)" }}>
      {img ? (
        <button
          ref={zoomBtnRef}
          type="button"
          onClick={() => setZoom(true)}
          aria-label={`画像を拡大表示: ${step.title}`}
          style={{ display: "block", width: "100%", padding: 0, border: 0, background: "none", cursor: "zoom-in", textAlign: "inherit" }}
        >
          {frame}
        </button>
      ) : (
        frame
      )}
      {!img ? (
        <div className="mono" style={{ fontSize: 11, color: "var(--muted)", marginTop: 6 }}>画像を準備中…</div>
      ) : null}
      {videoSrc ? (
        <button
          ref={videoOpenBtnRef}
          className="statuspill"
          onClick={onToggleVideo}
          style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Icon name="play" size={14} /> 動画でこの手順を見る
          {step.videoTimecodeMs != null ? ` · ${mmss(step.videoTimecodeMs)}` : ""}
        </button>
      ) : null}

      {/* Portal: .sline's content-visibility (layout containment) would otherwise trap fixed positioning */}
      {zoom && img ? createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`${step.title} の画像`}
          onClick={closeZoom}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              closeZoom();
            } else if (e.key === "Tab") {
              e.preventDefault(); // single focusable control — keep focus on 閉じる
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 60,
            display: "grid",
            placeItems: "center",
            padding: "min(4vmin,28px)",
            background: "rgba(8,9,12,.9)",
            cursor: "zoom-out",
          }}
        >
          <img
            src={img}
            alt={step.title}
            style={{ maxWidth: "100%", maxHeight: "92vh", objectFit: "contain", borderRadius: 8, boxShadow: "0 30px 80px -24px rgba(0,0,0,.9)" }}
          />
          <button
            ref={closeRef}
            type="button"
            onClick={closeZoom}
            aria-label="拡大表示を閉じる"
            style={{
              position: "absolute",
              top: "max(14px, env(safe-area-inset-top))",
              right: "max(14px, env(safe-area-inset-right))",
              minWidth: 56,
              minHeight: 56,
              borderRadius: 12,
              display: "grid",
              placeItems: "center",
              background: "rgba(0,0,0,.55)",
              color: "#F3F1E9",
              cursor: "pointer",
            }}
          >
            <Icon name="close" size={22} />
          </button>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

/** Read-only step body text: markdown caption (with official .hl terms) + cautions + table. */
export function StepText({ step, bodyStyle }: { step: StepWithCandidates; bodyStyle?: CSSProperties }) {
  return (
    <>
      <MarkdownText
        className="jp"
        style={{ fontSize: 17, lineHeight: 1.85, color: "var(--ink)", ...bodyStyle }}
        text={step.caption}
        terms={step.highlights?.terms}
      />
      {step.cautions.map((c, ci) => (
        <Banner key={ci} variant={cautionVariant(c.level)}>
          <span>{c.text}</span>
        </Banner>
      ))}
      {step.table ? (
        <table className="tbl" style={{ marginTop: 16, clear: "both" }}>
          <thead>
            <tr>{step.table.columns.map((col) => <th key={col}>{col}</th>)}</tr>
          </thead>
          <tbody>
            {step.table.rows.map((row, ri) => (
              <tr key={ri}>{row.map((cell, ci) => <td key={ci} className="jp">{cell}</td>)}</tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}
